<template>
  <modal-wrap
    :id="id"
    ref="listIntegrations"
    body-class="integrations-modal"
    :name="modalName"
    :header-class="'integrations-header'"
    :hide-footer="true"
    size="md"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">Integrations</h4>
      </div>
    </template>
    <template #default>
      <div>
        <div v-if="integrations.length" class="integrations">
          <div v-for="(integration, index) in integrations" :key="index" class="integration">
            <div class="integration-name d-flex">
              <div class="flex-column integration-label col-4">
                {{ getSystemDisplayName(integration.marketplace) }}
              </div>
              <div class="flex-column integration-button col-4">
                <button v-if="integration.id" class="btn btn-grey" @click="showDetails(integration)">Details</button>
              </div>
              <div class="flex-column integration-button col-4">
                <button v-if="integration.id" class="btn btn-danger" @click="destroyIntegration(integration)">
                  Disconnect
                </button>
                <button v-if="!integration.id" class="btn btn-green" @click="connectIntegration(integration)">
                  Connect
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="text-center">
            <b-spinner class="mt-5" />
            <p class="mt-3">Loading integrations...</p>
          </div>
        </div>
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type {
  Integration,
  IntegrationsModal,
  WooCommerceIntegrationModal,
  ShopifyIntegrationModal,
  OnGoingIntegrationModal,
  MagentoIntegrationModal,
  ViskanIntegrationModal,
} from '@/types/Models/Integration'
import { notify } from '@kyvg/vue3-notification'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, computed, watch, type ComputedRef } from 'vue'
import { MAGENTO, ONGOING, SHOPIFY, VISKAN, WOOCOMMERCE } from '@/const/IntegrationTypes'
import {
  INTEGRATION_DETAILS,
  INTEGRATIONS,
  ONGOING_WEBHOOK_SETTINGS,
  SHOPIFY_WEBHOOK_SETTINGS,
  VISKAN_SETTINGS,
  WOO_COMMERCE_KEYS,
  MAGENTO_KEYS,
} from '@/const/ModalTypes'
import { getSystemDisplayName } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'IntegrationsActionModal',
  components: { BSpinner, ModalWrap },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const modalStore = useVueNextModalStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()
    const modalName = INTEGRATIONS

    const integrations: ComputedRef<Integration[]> = computed(() => customersIntegrationsStore.integrations)
    const modalData: ComputedRef<IntegrationsModal> = computed(() => modalStore.modalPropsByName(modalName) || {})
    const customerId: ComputedRef<string> = computed(() => modalData.value?.id)

    const destroyIntegration = (integration: Integration) => {
      if (integration.marketplace === 'shopify' && integration.credentials?.access_token) {
        notify({
          group: 'error',
          text: 'Uninstall the app from your Shopify store. Integration and all personal data will be removed automatically.',
          duration: 6000,
        })
        return
      }
      openModal('confirmWrap', {
        title: 'The Integration will be disabled.',
        message: 'Are you sure you want to disable this integration?',
        okButtonText: 'Yes',
        onConfirm: () => {
          customersIntegrationsStore
            .destroy({ customerId: customerId.value, integrationId: integration.id })
            .then(() => {
              customersIntegrationsStore.fetch(customerId.value)
              notify({ group: 'success', text: 'The integration has been disabled' })
            })
        },
      })
    }

    const connectIntegration = (integration: Integration) => {
      switch (integration.marketplace) {
        case WOOCOMMERCE:
          return woocommerceSettings()
        case MAGENTO:
          return magentoSettings()
        case SHOPIFY:
          return shopifySettings()
        case ONGOING:
          return ongoingSettings()
        case VISKAN:
          return viskanSettings()
      }
    }

    const shopifySettings = () => {
      openModal(SHOPIFY_WEBHOOK_SETTINGS, {
        action: 'create',
        externalId: modalData.value.company.externalId || '',
        customerId: customerId.value || '',
        url: '',
      } as ShopifyIntegrationModal)
    }

    const ongoingSettings = () => {
      openModal(ONGOING_WEBHOOK_SETTINGS, {
        sandbox: false,
        customerId: customerId.value || '',
        warehouseId: '',
        key: '',
        secret: '',
      } as OnGoingIntegrationModal)
    }

    const woocommerceSettings = () => {
      openModal(WOO_COMMERCE_KEYS, {
        customerId: customerId.value,
        url: '',
        key: '',
        secret: '',
        importInterval: 60,
        importType: 'scheduled',
        active: true,
        defaultWeight: 500,
        defaultLength: 34,
        defaultWidth: 24,
        defaultHeight: 7,
        triggerOrderStatus: 'processing',
      } as WooCommerceIntegrationModal)
    }

    const magentoSettings = () => {
      openModal(MAGENTO_KEYS, {
        customerId: customerId.value,
        url: '',
        key: '',
        importInterval: 60,
        importType: 'scheduled',
        active: true,
        defaultWeight: 500,
        defaultLength: 34,
        defaultWidth: 24,
        defaultHeight: 7,
        configuration: {
          shipment_method: '',
        },
      } as MagentoIntegrationModal)
    }

    const viskanSettings = () => {
      openModal(VISKAN_SETTINGS, { customerId: customerId.value } as ViskanIntegrationModal)
    }

    const showDetails = (integration: Integration) => {
      openModal(INTEGRATION_DETAILS, { ...integration, customerId: customerId.value })
    }

    watch(customerId, () => {
      customersIntegrationsStore.reset()
      if (customerId.value) {
        customersIntegrationsStore.fetch(customerId.value)
      }
    })

    return {
      modalName,
      integrations,
      destroyIntegration,
      connectIntegration,
      showDetails,
      getSystemDisplayName,
    }
  },
})
</script>
